import { createContext, useContext, useState, useCallback, useEffect, useRef, type ReactNode } from 'react'
import { useAppSelector } from '../hooks/useAppDispatch'
import { useGoogleLogin } from '../hooks/useGoogleLogin'
import { useMicrosoftLogin } from '../hooks/useMicrosoftLogin'
import {
  STORAGE_KEYS, DOM_EVENTS,
  FIRST_VISIT_PROMPT_DELAY_MS,
  GUEST_ENGAGEMENT_INTERACTION_THRESHOLD,
  GUEST_SCROLL_PROMPT_THRESHOLD_PX,
} from '../lib/config'

interface LoginPromptState {
  trigger: (reason?: string) => void
}

const LoginPromptContext = createContext<LoginPromptState>({ trigger: () => {} })
export const useLoginPrompt = () => useContext(LoginPromptContext)

// ── Interaction counter helpers ───────────────────────────────
const getInteractions = () => parseInt(sessionStorage.getItem(STORAGE_KEYS.guestInteractions) ?? '0', 10)
const bumpInteractions = () => {
  const n = getInteractions() + 1
  sessionStorage.setItem(STORAGE_KEYS.guestInteractions, String(n))
  return n
}

// ── Provider ──────────────────────────────────────────────────
export function LoginPromptProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated, isLoading } = useAppSelector((s) => s.auth)
  const [isOpen, setIsOpen]           = useState(false)
  const [reason, setReason]           = useState<string | undefined>(undefined)
  const [isFirstVisit, setIsFirstVisit] = useState(false)

  const firstVisitFired = useRef(false)
  const engagementFired = useRef(!!sessionStorage.getItem(STORAGE_KEYS.engagementShown))

  // ── Action gate: called by buttons (comment, post, vote, etc.) ──
  const trigger = useCallback((msg?: string) => {
    if (isAuthenticated) return
    setIsFirstVisit(false)
    setReason(msg)
    setIsOpen(true)
  }, [isAuthenticated])

  // ── Auto-prompt 1: First visit after delay ───────────────────
  useEffect(() => {
    if (isAuthenticated || isLoading) return
    if (localStorage.getItem(STORAGE_KEYS.firstVisit)) return
    if (firstVisitFired.current) return

    const t = setTimeout(() => {
      if (firstVisitFired.current || isAuthenticated) return
      firstVisitFired.current = true
      setIsFirstVisit(true)
      setReason(undefined)
      setIsOpen(true)
    }, FIRST_VISIT_PROMPT_DELAY_MS)

    return () => clearTimeout(t)
  }, [isAuthenticated, isLoading])

  // ── Auto-prompt 2: Engagement trigger after N interactions ───
  useEffect(() => {
    if (isAuthenticated) return

    const onInteraction = () => {
      const count = bumpInteractions()
      const firstVisitSeen = !!localStorage.getItem(STORAGE_KEYS.firstVisit)
      const engagementSeen = !!sessionStorage.getItem(STORAGE_KEYS.engagementShown)

      if (count >= GUEST_ENGAGEMENT_INTERACTION_THRESHOLD && firstVisitSeen && !engagementSeen && !isOpen) {
        engagementFired.current = true
        sessionStorage.setItem(STORAGE_KEYS.engagementShown, '1')
        setIsFirstVisit(false)
        setReason("You've been exploring — sign in to get AI-powered answers and join the conversation")
        setIsOpen(true)
      }
    }

    const onScroll = () => {
      if (window.scrollY > GUEST_SCROLL_PROMPT_THRESHOLD_PX) {
        onInteraction()
        window.removeEventListener('scroll', onScroll)
      }
    }

    window.addEventListener(DOM_EVENTS.guestInteraction, onInteraction)
    window.addEventListener('scroll', onScroll, { passive: true })

    return () => {
      window.removeEventListener(DOM_EVENTS.guestInteraction, onInteraction)
      window.removeEventListener('scroll', onScroll)
    }
  }, [isAuthenticated, isOpen])

  // ── Close handlers ────────────────────────────────────────────
  const handleClose = () => {
    setIsOpen(false)
    if (isFirstVisit) {
      localStorage.setItem(STORAGE_KEYS.firstVisit, '1')
      setIsFirstVisit(false)
    }
  }

  // "Continue reading" — user chose to browse without signing in
  const handleContinueReading = () => {
    localStorage.setItem(STORAGE_KEYS.firstVisit, '1')
    sessionStorage.setItem(STORAGE_KEYS.guestInteractions, '0') // reset so engagement can fire later
    setIsOpen(false)
    setIsFirstVisit(false)
  }

  return (
    <LoginPromptContext.Provider value={{ trigger }}>
      {children}
      {isOpen && !isAuthenticated && (
        <LoginPromptModal
          reason={reason}
          isFirstVisit={isFirstVisit}
          onClose={handleClose}
          onContinueReading={handleContinueReading}
        />
      )}
    </LoginPromptContext.Provider>
  )
}

// ── Modal ─────────────────────────────────────────────────────
function LoginPromptModal({ reason, isFirstVisit, onClose, onContinueReading }: {
  reason?: string
  isFirstVisit: boolean
  onClose: () => void
  onContinueReading: () => void
}) {
  const { login } = useGoogleLogin()
  const { login: loginWithMicrosoft } = useMicrosoftLogin()

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [onClose])

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 10000,
        background: 'rgba(0,0,0,0.6)',
        backdropFilter: 'blur(6px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '1rem',
        animation: 'lpFade 0.18s ease',
      }}
    >
      <style>{`
        @keyframes lpFade  { from { opacity: 0 } to { opacity: 1 } }
        @keyframes lpSlide { from { opacity: 0; transform: translateY(20px) } to { opacity: 1; transform: translateY(0) } }
      `}</style>

      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--bg-card)',
          border: '1px solid var(--border)',
          borderRadius: '20px',
          padding: '2rem 1.75rem',
          width: '100%', maxWidth: '400px',
          textAlign: 'center',
          boxShadow: 'var(--shadow-elevated)',
          animation: 'lpSlide 0.22s ease',
          position: 'relative',
        }}
      >
        {/* Close */}
        <button onClick={onClose} style={{
          position: 'absolute', top: '1rem', right: '1rem',
          background: 'var(--bg-elevated)', border: '1px solid var(--border)',
          borderRadius: '50%', width: '28px', height: '28px',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer', color: 'var(--text-muted)', fontSize: '0.8rem',
        }}>✕</button>

        {/* Icon */}
        <div style={{
          width: '56px', height: '56px', borderRadius: '16px',
          background: 'var(--accent-gradient)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          margin: '0 auto 1.1rem', fontSize: '1.5rem', color: 'white',
        }}>
          {isFirstVisit ? '👋' : '🚀'}
        </div>

        <h2 style={{ margin: '0 0 0.5rem', fontSize: '1.2rem', fontWeight: 700, color: 'var(--text-primary)' }}>
          {isFirstVisit ? 'Welcome to Ekkam AI' : 'Join the conversation'}
        </h2>

        <p style={{ margin: '0 0 1rem', fontSize: '0.875rem', color: 'var(--text-muted)', lineHeight: 1.55 }}>
          {reason ?? 'Ask questions, get AI-powered answers from specialist agents, and connect with a community of curious minds.'}
        </p>

        {/* Feature pills */}
        <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', justifyContent: 'center', marginBottom: '1.25rem' }}>
          {['🤖 AI answers', '💬 Comment', '⬆️ Vote', '🔔 Alerts'].map((f) => (
            <span key={f} style={{
              background: 'var(--bg-elevated)', border: '1px solid var(--border)',
              borderRadius: '20px', padding: '0.22rem 0.6rem',
              fontSize: '0.75rem', color: 'var(--text-secondary)',
            }}>{f}</span>
          ))}
        </div>

        {/* Google CTA */}
        <button onClick={() => { login(); onClose() }} style={{
          width: '100%', padding: '0.78rem 1rem',
          background: 'white', color: '#111',
          border: '1px solid #ddd', borderRadius: '10px',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.75rem',
          cursor: 'pointer', fontWeight: 600, fontSize: '0.9rem',
          boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
          marginBottom: '0.75rem',
        }}>
          <svg width="18" height="18" viewBox="0 0 18 18">
            <path fill="#4285F4" d="M16.51 8H8.98v3h4.3c-.18 1-.74 1.48-1.6 2.04v2.01h2.6a7.8 7.8 0 0 0 2.38-5.88c0-.57-.05-.66-.15-1.18z"/>
            <path fill="#34A853" d="M8.98 17c2.16 0 3.97-.72 5.3-1.94l-2.6-2a4.8 4.8 0 0 1-7.18-2.54H1.83v2.07A8 8 0 0 0 8.98 17z"/>
            <path fill="#FBBC05" d="M4.5 10.52a4.8 4.8 0 0 1 0-3.04V5.41H1.83a8 8 0 0 0 0 7.18l2.67-2.07z"/>
            <path fill="#EA4335" d="M8.98 4.18c1.17 0 2.23.4 3.06 1.2l2.3-2.3A8 8 0 0 0 1.83 5.4L4.5 7.49a4.77 4.77 0 0 1 4.48-3.3z"/>
          </svg>
          Continue with Google
        </button>

        {/* Microsoft CTA */}
        <button onClick={() => { loginWithMicrosoft(); onClose() }} style={{
          width: '100%', padding: '0.78rem 1rem',
          background: 'white', color: '#111',
          border: '1px solid #ddd', borderRadius: '10px',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.75rem',
          cursor: 'pointer', fontWeight: 600, fontSize: '0.9rem',
          boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
          marginBottom: '0.75rem',
        }}>
          <svg width="18" height="18" viewBox="0 0 18 18">
            <rect x="1" y="1" width="7.5" height="7.5" fill="#F25022"/>
            <rect x="9.5" y="1" width="7.5" height="7.5" fill="#7FBA00"/>
            <rect x="1" y="9.5" width="7.5" height="7.5" fill="#00A4EF"/>
            <rect x="9.5" y="9.5" width="7.5" height="7.5" fill="#FFB900"/>
          </svg>
          Continue with Microsoft
        </button>

        {isFirstVisit ? (
          <button onClick={onContinueReading} style={{
            width: '100%', padding: '0.65rem',
            background: 'transparent', border: '1px solid var(--border)',
            borderRadius: '10px', cursor: 'pointer',
            fontSize: '0.875rem', color: 'var(--text-muted)',
          }}>
            Continue reading without signing in →
          </button>
        ) : (
          <button onClick={onClose} style={{
            width: '100%', padding: '0.65rem',
            background: 'transparent', border: 'none',
            cursor: 'pointer', fontSize: '0.825rem', color: 'var(--text-muted)',
          }}>
            Maybe later
          </button>
        )}

        <p style={{ fontSize: '0.7rem', color: 'var(--text-muted)', margin: '0.75rem 0 0', lineHeight: 1.4 }}>
          Free forever · No spam · Sign out any time
        </p>
      </div>
    </div>
  )
}
